// Feriados do Brasil calculados no próprio app (nenhum dado vai para o servidor).
// Fontes conferidas para 2026: Portaria MGI nº 11.460/2025 (nacionais e pontos facultativos federais),
// leis estaduais de cada UF (datas magnas e feriados estaduais) e os calendários oficiais de 2026 das
// prefeituras de Campo Grande, Curitiba e São Paulo. Capitais (0.32): leis e decretos municipais e calendários
// de 2026 dos tribunais; datas sem confirmação oficial ficaram de fora (nesses casos vale o ponto facultativo nacional).
// Datas móveis (Carnaval, Sexta-feira Santa, Corpus Christi) saem do cálculo da Páscoa, ano a ano.
// Outros países vêm da base pública Nager.Date (lib/feriadosMundo.ts).

import { N_ } from './i18n';
import { feriadosExternos } from './feriadosMundo';

export type TipoFeriado = 'nacional' | 'estadual' | 'regional' | 'municipal' | 'facultativo';
/** pais: código do país (BR, US, PT...). local: Brasil, a UF, a cidade ou as regiões do país. */
export type Feriado = { data: string; nome: string; tipo: TipoFeriado; local: string; pais: string };

/** Preferências de feriados do usuário (Ajustes > Visualizar feriados).
 *  regioes.BR guarda as UFs ("SP") e as cidades ("sao-paulo-sp"); nos outros países, os códigos das regiões ("US-CA"). */
export type FeriadosPref = { paises: string[]; regioes: Record<string, string[]>; semNacionais?: boolean };

/** As 27 unidades da federação. */
export const ESTADOS_BR: { uf: string; nome: string }[] = [
  { uf: 'AC', nome: 'Acre' }, { uf: 'AL', nome: 'Alagoas' }, { uf: 'AP', nome: 'Amapá' }, { uf: 'AM', nome: 'Amazonas' },
  { uf: 'BA', nome: 'Bahia' }, { uf: 'CE', nome: 'Ceará' }, { uf: 'DF', nome: 'Distrito Federal' }, { uf: 'ES', nome: 'Espírito Santo' },
  { uf: 'GO', nome: 'Goiás' }, { uf: 'MA', nome: 'Maranhão' }, { uf: 'MT', nome: 'Mato Grosso' }, { uf: 'MS', nome: 'Mato Grosso do Sul' },
  { uf: 'MG', nome: 'Minas Gerais' }, { uf: 'PA', nome: 'Pará' }, { uf: 'PB', nome: 'Paraíba' }, { uf: 'PR', nome: 'Paraná' },
  { uf: 'PE', nome: 'Pernambuco' }, { uf: 'PI', nome: 'Piauí' }, { uf: 'RJ', nome: 'Rio de Janeiro' }, { uf: 'RN', nome: 'Rio Grande do Norte' },
  { uf: 'RS', nome: 'Rio Grande do Sul' }, { uf: 'RO', nome: 'Rondônia' }, { uf: 'RR', nome: 'Roraima' }, { uf: 'SC', nome: 'Santa Catarina' },
  { uf: 'SP', nome: 'São Paulo' }, { uf: 'SE', nome: 'Sergipe' }, { uf: 'TO', nome: 'Tocantins' },
];
/** Feriados municipais das 27 capitais: [mês, dia, nome] ou 'corpus' (Corpus Christi como feriado na cidade). */
type DiaCidade = [number, number, string] | 'corpus';
const CAPITAIS: { id: string; nome: string; uf: string; f: DiaCidade[] }[] = [
  { id: 'aracaju-se', nome: 'Aracaju', uf: 'SE', f: [[3, 17, 'Aniversário de Aracaju'], [6, 24, 'São João'], [12, 8, 'Nossa Senhora da Conceição'], 'corpus'] },
  { id: 'belem-pa', nome: 'Belém', uf: 'PA', f: [[12, 8, 'Nossa Senhora da Conceição'], 'corpus'] },
  { id: 'belo-horizonte-mg', nome: 'Belo Horizonte', uf: 'MG', f: [[8, 15, 'Assunção de Nossa Senhora'], [12, 8, 'Nossa Senhora da Conceição'], 'corpus'] },
  { id: 'boa-vista-rr', nome: 'Boa Vista', uf: 'RR', f: [[1, 20, 'São Sebastião'], [6, 29, 'São Pedro'], [7, 9, 'Aniversário de Boa Vista'], [12, 8, 'Nossa Senhora da Conceição'], 'corpus'] },
  { id: 'brasilia-df', nome: 'Brasília', uf: 'DF', f: [] },
  { id: 'campo-grande-ms', nome: 'Campo Grande', uf: 'MS', f: [[6, 13, 'Santo Antônio'], [8, 26, 'Aniversário de Campo Grande']] },
  { id: 'cuiaba-mt', nome: 'Cuiabá', uf: 'MT', f: [[4, 8, 'Aniversário de Cuiabá'], [12, 8, 'Nossa Senhora da Conceição'], 'corpus'] },
  { id: 'curitiba-pr', nome: 'Curitiba', uf: 'PR', f: [[9, 8, 'Nossa Senhora da Luz dos Pinhais'], 'corpus'] },
  { id: 'florianopolis-sc', nome: 'Florianópolis', uf: 'SC', f: [[3, 23, 'Emancipação Política de Florianópolis'], 'corpus'] },
  { id: 'fortaleza-ce', nome: 'Fortaleza', uf: 'CE', f: [[4, 13, 'Aniversário de Fortaleza'], [8, 15, 'Nossa Senhora da Assunção'], 'corpus'] },
  { id: 'goiania-go', nome: 'Goiânia', uf: 'GO', f: [[5, 24, 'Nossa Senhora Auxiliadora'], [10, 24, 'Aniversário de Goiânia'], 'corpus'] },
  { id: 'joao-pessoa-pb', nome: 'João Pessoa', uf: 'PB', f: [[6, 24, 'São João'], [8, 5, 'Nossa Senhora das Neves'], [12, 8, 'Nossa Senhora da Conceição']] },
  { id: 'macapa-ap', nome: 'Macapá', uf: 'AP', f: [[2, 4, 'Aniversário de Macapá'], [12, 8, 'Nossa Senhora da Conceição'], 'corpus'] },
  { id: 'maceio-al', nome: 'Maceió', uf: 'AL', f: [[8, 27, 'Nossa Senhora dos Prazeres'], [12, 8, 'Nossa Senhora da Conceição'], 'corpus'] },
  { id: 'manaus-am', nome: 'Manaus', uf: 'AM', f: [[10, 24, 'Aniversário de Manaus'], [12, 8, 'Nossa Senhora da Conceição']] },
  { id: 'natal-rn', nome: 'Natal', uf: 'RN', f: [[1, 6, 'Dia de Reis'], [11, 21, 'Nossa Senhora da Apresentação']] },
  { id: 'palmas-to', nome: 'Palmas', uf: 'TO', f: [[3, 19, 'São José'], [5, 20, 'Aniversário de Palmas']] },
  { id: 'porto-alegre-rs', nome: 'Porto Alegre', uf: 'RS', f: [[2, 2, 'Nossa Senhora dos Navegantes'], 'corpus'] },
  { id: 'porto-velho-ro', nome: 'Porto Velho', uf: 'RO', f: [[1, 24, 'Instalação do Município de Porto Velho'], [10, 2, 'Criação do Município de Porto Velho']] },
  { id: 'recife-pe', nome: 'Recife', uf: 'PE', f: [[3, 12, 'Aniversário do Recife'], [6, 24, 'São João'], [7, 16, 'Nossa Senhora do Carmo'], [12, 8, 'Nossa Senhora da Conceição']] },
  { id: 'rio-branco-ac', nome: 'Rio Branco', uf: 'AC', f: [[12, 28, 'Aniversário de Rio Branco']] },
  { id: 'rio-de-janeiro-rj', nome: 'Rio de Janeiro', uf: 'RJ', f: [[1, 20, 'São Sebastião']] },
  { id: 'salvador-ba', nome: 'Salvador', uf: 'BA', f: [[6, 24, 'São João'], [12, 8, 'Nossa Senhora da Conceição da Praia'], 'corpus'] },
  { id: 'sao-luis-ma', nome: 'São Luís', uf: 'MA', f: [[9, 8, 'Aniversário de São Luís'], [12, 8, 'Nossa Senhora da Conceição']] },
  { id: 'sao-paulo-sp', nome: 'São Paulo', uf: 'SP', f: [[1, 25, 'Aniversário de São Paulo'], 'corpus'] },
  { id: 'teresina-pi', nome: 'Teresina', uf: 'PI', f: [[8, 16, 'Aniversário de Teresina'], [12, 8, 'Nossa Senhora da Conceição'], 'corpus'] },
  { id: 'vitoria-es', nome: 'Vitória', uf: 'ES', f: [[9, 8, 'Nossa Senhora da Vitória'], 'corpus'] },
];
/** Cidades com calendário municipal no app (as 27 capitais). */
export const CIDADES_BR: { id: string; nome: string }[] = CAPITAIS.map(c => ({ id: c.id, nome: `${c.nome} (${c.uf})` }));

const p2 = (n: number) => String(n).padStart(2, '0');
const ymd = (y: number, m: number, d: number) => `${y}-${p2(m)}-${p2(d)}`;

/** Domingo de Páscoa (algoritmo de Meeus/Jones/Butcher, calendário gregoriano). */
export function pascoa(y: number) {
  const a = y % 19, b = Math.floor(y / 100), c = y % 100, d = Math.floor(b / 4), e = b % 4;
  const f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
  const mes = Math.floor((h + l - 7 * m + 114) / 31), dia = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(y, mes - 1, dia);
}
const somar = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return ymd(x.getFullYear(), x.getMonth() + 1, x.getDate()); };

/** Feriados estaduais por UF: [mês, dia, nome]. Os nomes ficam em português (são nomes próprios).
 *  Goiás, Minas Gerais (21/4 já é nacional), Mato Grosso, Paraná e Santa Catarina não têm feriado estadual próprio. */
const ESTADUAIS: Record<string, [number, number, string][]> = {
  AC: [[1, 20, 'Dia do Católico'], [1, 23, 'Dia do Evangélico'], [3, 8, 'Dia Internacional da Mulher'], [6, 15, 'Aniversário do Acre'],
    [9, 5, 'Dia da Amazônia'], [11, 17, 'Assinatura do Tratado de Petrópolis']],
  AL: [[6, 24, 'São João'], [6, 29, 'São Pedro'], [9, 16, 'Emancipação Política de Alagoas'], [11, 30, 'Dia do Evangélico']],
  AP: [[3, 19, 'São José'], [5, 15, 'Dia de Cabralzinho'], [9, 13, 'Criação do Território do Amapá']],
  AM: [[9, 5, 'Elevação do Amazonas à categoria de Província']],
  BA: [[7, 2, 'Independência da Bahia']],
  CE: [[3, 19, 'São José'], [3, 25, 'Data Magna do Ceará (Abolição da Escravidão)']],
  DF: [[11, 30, 'Dia do Evangélico']],
  ES: [[4, 28, 'Nossa Senhora da Penha']],
  MA: [[7, 28, 'Adesão do Maranhão à Independência']],
  MS: [[10, 11, 'Criação do Estado de MS']],
  PA: [[8, 15, 'Adesão do Pará à Independência']],
  PB: [[8, 5, 'Fundação do Estado da Paraíba']],
  PE: [[3, 6, 'Revolução Pernambucana de 1817']],
  PI: [[10, 19, 'Dia do Piauí']],
  RJ: [[4, 23, 'Dia de São Jorge']],
  RN: [[10, 3, 'Mártires de Cunhaú e Uruaçu']],
  RS: [[9, 20, 'Revolução Farroupilha']],
  RO: [[1, 4, 'Criação do Estado de Rondônia']],
  RR: [[10, 5, 'Criação do Estado de Roraima']],
  SP: [[7, 9, 'Revolução Constitucionalista']],
  SE: [[7, 8, 'Emancipação Política de Sergipe']],
  TO: [[8, 15, 'Dia do Senhor do Bonfim'], [9, 8, 'Nossa Senhora da Natividade'], [10, 5, 'Criação do Estado do Tocantins']],
};

function doAnoBR(y: number, cal: string): Feriado[] {
  const P = pascoa(y);
  const corpus = somar(P, 60);
  const BR = 'BR';
  if (ESTADUAIS[cal]) {
    const local = ESTADOS_BR.find(e => e.uf === cal)?.nome ?? cal;
    return ESTADUAIS[cal].map(([m, d, nome]) => ({ data: ymd(y, m, d), nome, tipo: 'estadual', local, pais: BR }));
  }
  const cap = CAPITAIS.find(c => c.id === cal);
  if (cap) {
    // Brasília não tem município: valem os feriados do Distrito Federal
    if (!cap.f.length) return doAnoBR(y, cap.uf);
    return cap.f.map(f => f === 'corpus'
      ? { data: corpus, nome: N_('Corpus Christi'), tipo: 'municipal' as const, local: cap.nome, pais: BR }
      : { data: ymd(y, f[0], f[1]), nome: f[2], tipo: 'municipal' as const, local: cap.nome, pais: BR });
  }
  switch (cal) {
    case 'BR': return [
      { data: ymd(y, 1, 1), nome: N_('Confraternização Universal'), tipo: 'nacional', local: N_('Brasil'), pais: BR },
      { data: somar(P, -48), nome: N_('Carnaval'), tipo: 'facultativo', local: N_('Brasil'), pais: BR },
      { data: somar(P, -47), nome: N_('Carnaval'), tipo: 'facultativo', local: N_('Brasil'), pais: BR },
      { data: somar(P, -46), nome: N_('Quarta-feira de Cinzas (até 14h)'), tipo: 'facultativo', local: N_('Brasil'), pais: BR },
      { data: somar(P, -2), nome: N_('Paixão de Cristo'), tipo: 'nacional', local: N_('Brasil'), pais: BR },
      { data: ymd(y, 4, 21), nome: N_('Tiradentes'), tipo: 'nacional', local: N_('Brasil'), pais: BR },
      { data: ymd(y, 5, 1), nome: N_('Dia do Trabalho'), tipo: 'nacional', local: N_('Brasil'), pais: BR },
      { data: corpus, nome: N_('Corpus Christi'), tipo: 'facultativo', local: N_('Brasil'), pais: BR },
      { data: ymd(y, 9, 7), nome: N_('Independência do Brasil'), tipo: 'nacional', local: N_('Brasil'), pais: BR },
      { data: ymd(y, 10, 12), nome: N_('Nossa Senhora Aparecida'), tipo: 'nacional', local: N_('Brasil'), pais: BR },
      { data: ymd(y, 11, 2), nome: N_('Finados'), tipo: 'nacional', local: N_('Brasil'), pais: BR },
      { data: ymd(y, 11, 15), nome: N_('Proclamação da República'), tipo: 'nacional', local: N_('Brasil'), pais: BR },
      { data: ymd(y, 11, 20), nome: N_('Zumbi e Consciência Negra'), tipo: 'nacional', local: N_('Brasil'), pais: BR },
      { data: ymd(y, 12, 25), nome: N_('Natal'), tipo: 'nacional', local: N_('Brasil'), pais: BR },
    ];
    default: return [];
  }
}

/** Todos os feriados de um ano, de todos os países e regiões escolhidos (sem ordem). */
function doAno(ano: number, pref: FeriadosPref): Feriado[] {
  const out: Feriado[] = [];
  for (const pais of pref.paises) {
    const reg = pref.regioes[pais] ?? [];
    if (pais === 'BR') { for (const c of [...(pref.semNacionais ? [] : ['BR']), ...reg.filter(c => c !== 'BR')]) out.push(...doAnoBR(ano, c)); }
    else out.push(...feriadosExternos(pais, ano, reg));
  }
  return out;
}

/** Remove repetidos e aplica a regra: no mesmo dia e país, feriado vence ponto facultativo. */
function doDiaDe(lista: Feriado[], data: string): Feriado[] {
  const doDia = lista.filter(f => f.data === data);
  const r: Feriado[] = [];
  for (const pais of [...new Set(doDia.map(f => f.pais))]) {
    const dp = doDia.filter(f => f.pais === pais);
    const feriados = dp.filter(f => f.tipo !== 'facultativo');
    r.push(...(feriados.length ? feriados.filter((f, i, a) => a.findIndex(x => x.nome === f.nome && x.local === f.local) === i) : dp.slice(0, 1)));
  }
  return r;
}

const cache = new Map<string, Feriado[]>();
const chave = (ano: number, pref: FeriadosPref, versao: number) => `${ano}|${versao}|${JSON.stringify(pref)}`;
function listaDoAno(ano: number, pref: FeriadosPref, versao: number) {
  const k = chave(ano, pref, versao);
  let l = cache.get(k);
  if (!l) { l = doAno(ano, pref); if (cache.size > 40) cache.clear(); cache.set(k, l); }
  return l;
}

/** Feriados de uma data. "versao" muda quando chegam dados de outro país (força recalcular). */
export function feriadosDoDia(data: string, pref: FeriadosPref, versao = 0): Feriado[] {
  return doDiaDe(listaDoAno(Number(data.slice(0, 4)), pref, versao), data);
}

/** Todos os feriados de um ano, em ordem de data. */
export function feriadosDoAno(ano: number, pref: FeriadosPref, versao = 0): Feriado[] {
  const lista = listaDoAno(ano, pref, versao);
  return [...new Set(lista.map(f => f.data))].sort().flatMap(d => doDiaDe(lista, d));
}

export const ROTULO: Record<TipoFeriado, string> = {
  nacional: N_('Feriado nacional'), estadual: N_('Feriado estadual'), regional: N_('Feriado regional'),
  municipal: N_('Feriado municipal'), facultativo: N_('Ponto facultativo'),
};
/** Rótulo curto da lista anual: "Nacional: segunda-feira, 25 de janeiro de 2027 · Nome". */
export const ROTULO_CURTO: Record<TipoFeriado, string> = {
  nacional: N_('Nacional'), estadual: N_('Estadual'), regional: N_('Regional'), municipal: N_('Municipal'), facultativo: N_('Facultativo'),
};

/** Bandeira (emoji) de um código de país. */
export const bandeira = (pais: string) => /^[A-Z]{2}$/.test(pais)
  ? String.fromCodePoint(...[...pais].map(c => 0x1f1e6 + c.charCodeAt(0) - 65)) : '🏳️';

/** Preferência padrão: Brasil + o que o suporte liberou pelo CPF (como era até a versão 0.10). */
export function prefPadrao(calendariosLiberados: string[], ilimitado: boolean, pais = 'BR'): FeriadosPref {
  if (pais !== 'BR' && !ilimitado && !calendariosLiberados.length) return { paises: [pais], regioes: {} };   // app usado em outro país
  const extras = ilimitado ? ['MS', 'PR', 'SP', 'campo-grande-ms', 'curitiba-pr', 'sao-paulo-sp'] : calendariosLiberados.filter(c => c !== 'BR');
  return { paises: ['BR'], regioes: { BR: extras } };
}
